import { Button, cn } from '@chopchop-se/ui';
import { DELIVERY_CUTOFF_TIMEZONE, addIsoDays, type PortalDeliveryDate } from '@warehouse/shared';
import { ChevronLeft, ChevronRight, Info, TriangleAlert } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The order-placement calendar — ported from the old WMS
 * (`src/components/orders/delivery-calendar/DeliveryCalendar.tsx`).
 *
 * Kept from the port: the 120-day highlight window starting TOMORROW, greying
 * out non-delivery days, dropping dates whose order-by cutoff has passed, and
 * the bilingual Swedish + Chinese cutoff notice under the picker.
 *
 * Fixed from the port: the selectable set is computed by the SERVER
 * (`GET /api/portal/delivery-dates`, which runs the one shared cutoff
 * implementation), so the calendar can no longer disagree with what the API
 * will accept, and holiday moves are honoured instead of silently bypassing the
 * cutoff. Dates are handled as bare `YYYY-MM-DD` strings throughout — the old
 * code mixed instants and calendar dates, which shifted every highlight back a
 * weekday after local midnight (recon §6.5).
 */

export interface DeliveryCalendarProps {
  dates: readonly PortalDeliveryDate[];
  passed?: readonly string[];
  value: string | null;
  onChange: (date: string) => void;
  scheduleMissing?: boolean;
  loading?: boolean;
  /** Rendered instead of the notice when nothing is selected yet. */
  hint?: string;
}

const WEEK_STARTS_MONDAY_OFFSET = 1;

function monthOf(date: string): string {
  return date.slice(0, 7);
}

function firstOfMonth(month: string): string {
  return `${month}-01`;
}

function shiftMonth(month: string, delta: number): string {
  const [year, mon] = month.split('-').map(Number);
  const index = (year ?? 1970) * 12 + (mon ?? 1) - 1 + delta;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`;
}

function daysInMonth(month: string): string[] {
  const [year, mon] = month.split('-').map(Number);
  const count = new Date(Date.UTC(year ?? 1970, mon ?? 1, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** JS weekday 0=Sun..6=Sat of a calendar date, timezone-independent. */
function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

/** Monday-first column index (0..6). */
function columnOf(date: string): number {
  return (weekdayOf(date) + 7 - WEEK_STARTS_MONDAY_OFFSET) % 7;
}

export function formatDayLong(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${date}T00:00:00Z`));
}

/** A deadline is an INSTANT — always rendered in Swedish wall-clock time. */
export function formatDeadline(iso: string, locale: string): string {
  const at = new Date(iso);
  const day = new Intl.DateTimeFormat(locale, {
    timeZone: DELIVERY_CUTOFF_TIMEZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(at);
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: DELIVERY_CUTOFF_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at);
  return `${day} ${time}`;
}

export function DeliveryCalendar({
  dates,
  passed = [],
  value,
  onChange,
  scheduleMissing = false,
  loading = false,
  hint,
}: DeliveryCalendarProps): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);

  const selectable = useMemo(() => new Map(dates.map((d) => [d.date, d] as const)), [dates]);
  const passedSet = useMemo(() => new Set(passed), [passed]);

  // Open on the month of the selection, else the first month that still has
  // something orderable in it — the old calendar's behaviour.
  const initialMonth = value ? monthOf(value) : dates[0] ? monthOf(dates[0].date) : monthOf(todayIso());
  const [month, setMonth] = useState(initialMonth);

  const grid = useMemo(() => {
    const days = daysInMonth(month);
    const first = days[0] ?? firstOfMonth(month);
    const lead = columnOf(first);
    const cells: (string | null)[] = Array.from({ length: lead }, () => null);
    cells.push(...days);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const selected = value ? selectable.get(value) : undefined;

  if (scheduleMissing) {
    return (
      <div
        className="flex items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        role="alert"
        data-testid="delivery-calendar-schedule-missing"
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>{t('salesOrders:calendar.scheduleMissing')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="delivery-calendar">
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('salesOrders:calendar.previousMonth')}
          onClick={() => {
            setMonth(shiftMonth(month, -1));
          }}
        >
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
        <span className="font-medium capitalize">
          {new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
            new Date(`${firstOfMonth(month)}T00:00:00Z`),
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('salesOrders:calendar.nextMonth')}
          onClick={() => {
            setMonth(shiftMonth(month, 1));
          }}
        >
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[0.7rem] uppercase tracking-wide text-ink-muted">
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <span key={day}>{t(`salesOrders:weekdayShort.${day}`)}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" role="grid">
        {grid.map((date, index) => {
          if (!date) return <span key={`pad-${index}`} aria-hidden />;
          const entry = selectable.get(date);
          const isPassed = passedSet.has(date);
          const isSelected = date === value;
          return (
            <button
              key={date}
              type="button"
              role="gridcell"
              disabled={!entry || loading}
              aria-pressed={isSelected}
              title={entry?.movedFrom ? t('salesOrders:calendar.movedFrom', { date: entry.movedFrom }) : undefined}
              onClick={() => {
                onChange(date);
              }}
              className={cn(
                'relative rounded-lg py-2 text-sm transition-colors',
                entry
                  ? 'bg-brand-600/[0.08] font-medium text-ink hover:bg-brand-600/20'
                  : isPassed
                    ? 'text-ink-muted/50 line-through'
                    : 'text-ink-muted/40',
                isSelected && 'bg-brand-600 text-white hover:bg-brand-700',
              )}
            >
              {Number(date.slice(8))}
              {entry?.movedFrom ? (
                <span className="absolute right-1 top-1 size-1.5 rounded-full bg-amber-500" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-ink-muted">{t('salesOrders:calendar.passedHint')}</p>

      {selected ? (
        <div
          className="flex flex-col gap-1 rounded-xl border border-surface-border bg-surface-subtle px-4 py-3 text-sm"
          data-testid="delivery-calendar-notice"
        >
          <span className="flex items-start gap-2">
            <Info className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
            <span>
              {selected.dueAt
                ? t('salesOrders:calendar.noticeSv', {
                    deadline: formatDeadline(selected.dueAt, 'sv-SE'),
                    delivery: formatDayLong(selected.date, 'sv-SE'),
                  })
                : formatDayLong(selected.date, 'sv-SE')}
            </span>
          </span>
          {selected.dueAt ? (
            <span className="pl-6 text-ink-muted">
              {t('salesOrders:calendar.noticeZh', {
                deadline: formatDeadline(selected.dueAt, 'zh-CN'),
                delivery: formatDayLong(selected.date, 'zh-CN'),
              })}
            </span>
          ) : null}
          {selected.movedFrom ? (
            <span className="pl-6 text-amber-700">
              {t('salesOrders:calendar.movedFrom', { date: selected.movedFrom })}
            </span>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-ink-muted">{hint ?? t('salesOrders:calendar.noDates')}</p>
      )}
    </div>
  );
}

function todayIso(): string {
  const now = new Date();
  return addIsoDays(
    `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`,
    0,
  );
}
